// js/sections/page-footer-button.js

const observer = function observeElementAtTop(el, offset = "0px 0px 0px 0px", callback, threshold = 0) {
  let margin = offset.includes(" ") ? offset : `-${offset}px 0px 0px 0px`;
  const observer = new IntersectionObserver(
    ([entry]) => {
      callback(entry);
    },
    {
      root: null,
      rootMargin: margin,
      threshold: threshold,
    },
  );

  observer.observe(el);
  return observer;
};

function isView(element, callback, options = {}) {
  const { margin = "0px", amount = 0, root = null, once = false } = options;

  let hasTriggered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isInView = entry.intersectionRatio >= amount;

        if (once && hasTriggered && !isInView) {
          return;
        }

        if (isInView && once) {
          hasTriggered = true;
        }

        callback(isInView, entry);
      });
    },
    {
      root,
      rootMargin: margin,
      threshold: typeof amount === "number" ? [amount] : amount,
    },
  );

  observer.observe(element);

  return () => observer.unobserve(element);
}

var StickyNav = class extends HTMLElement {
  constructor() {
    super();
    this.menuItems = [];
    this.currentIndex = 0;
    this._scrollHandler = null;
    this._headerContainer = null;
    this._sectionWrapper = null;
    this._lastScrollY = 0;
    this._isHeaderVisible = true;
    this._scrollEndTimer = null;
    this._isProgrammaticScrolling = false;
    this._headerResizeObserver = null;
    this._loadHandler = null;
    this._visibilityHandler = null;
    this._lastSectionTop = null;
    this._lastHeaderHeight = null;
    this._hamburgerBtn = null;
    this._menuDrawer = null;
    this._navFocusScrollCleanups = [];
  }

  connectedCallback() {
    this._sectionWrapper = this.closest(".section-page-footer-button") || this.closest(".section-page-footer-button");
    this._init();
    this._setupScrollListener();
    this._setupEventListeners();
    this._setupVisibilityListener();
  }

  disconnectedCallback() {
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._headerResizeObserver) {
      this._headerResizeObserver.disconnect();
    }
    if (this._loadHandler) {
      window.removeEventListener("load", this._loadHandler);
    }
    if (this._visibilityHandler) {
      document.removeEventListener("visibilitychange", this._visibilityHandler);
    }
    if (this._scrollEndTimer) {
      window.clearTimeout(this._scrollEndTimer);
      this._scrollEndTimer = null;
    }
    if (this._navFocusScrollCleanups.length) {
      this._navFocusScrollCleanups.forEach(({ nav, handler }) => {
        nav.removeEventListener("focusin", handler);
      });
      this._navFocusScrollCleanups = [];
    }
  }

  _init() {
    const drawerByTargetId = new Map();
    this.querySelectorAll(".page-footer-menu-drawer-item").forEach((el) => {
      const id = el.getAttribute("data-target-id");
      if (id) {
        drawerByTargetId.set(id, el);
      }
    });

    this.menuItems = Array.from(this.querySelectorAll(".page-footer-button-item")).map((item) => {
      const targetId = item.getAttribute("data-target-id");
      return {
        element: item,
        drawerElement: targetId ? drawerByTargetId.get(targetId) ?? null : null,
        title: item.getAttribute("data-title") || item.textContent.trim(),
        targetId,
        targetElement: null,
      };
    });

    this.menuItems.forEach((item) => {
      if (item.targetId) {
        item.targetElement = document.getElementById(item.targetId);
      }
    });

    this._hamburgerBtn = this.querySelector(".page-footer-hamburger");
    this._menuDrawer = this.querySelector(".page-footer-menu-drawer");

    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      this._setHeaderHeight(this._headerContainer.getBoundingClientRect().height);
      this._setupHeaderObserver();
    }

    this._checkFirstScreenVisibility();
    this._updateActiveItem(0);
    this._syncSectionTop();
    this._syncSectionTopAfterTransition();

    this._loadHandler = () => {
      this._checkFirstScreenVisibility();
      this._syncSectionTopAfterTransition();
    };
    window.addEventListener("load", this._loadHandler, { once: true });
  }

  _getFirstScreenElement() {
    const mainElement = document.querySelector("main");
    return mainElement?.firstElementChild ?? null;
  }

  _isPastFirstScreenCenter() {
    const firstScreen = this._getFirstScreenElement();
    if (!firstScreen) return true;

    const rect = firstScreen.getBoundingClientRect();
    return rect.bottom < window.innerHeight / 2;
  }

  /** 与 _isPastFirstScreenCenter 阈值一致：首屏底边刚越过视口中线 */
  _getSecondScreenScrollTop() {
    const firstScreen = this._getFirstScreenElement();
    if (!firstScreen) return 0;

    const bottomDoc = firstScreen.getBoundingClientRect().bottom + window.pageYOffset;
    return Math.max(0, Math.ceil(bottomDoc - window.innerHeight / 2 + 1));
  }

  _checkFirstScreenVisibility() {
    const isPastCenter = this._isPastFirstScreenCenter();

    // 二级菜单和主导航栏不能同时存在
    const shouldShowSecondary = isPastCenter && !this._isHeaderVisible;

    if (shouldShowSecondary) {
      if (this._sectionWrapper) this._sectionWrapper.classList.add("is-visible");
      this.classList.add("is-visible");
    } else {
      if (this._sectionWrapper) this._sectionWrapper.classList.remove("is-visible");
      this.classList.remove("is-visible");
    }

    return isPastCenter;
  }

  _setupScrollListener() {
    this._scrollHandler = this._throttle(() => {
      this._handleHeaderVisibility();
    }, 16);

    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    this._resizeHandler = this._throttle(() => {
      this._handleHeaderVisibilityResize();
    }, 50);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  _handleHeaderVisibilityResize() {
    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      this._setHeaderHeight(this._headerContainer.getBoundingClientRect().height);

      const currentScrollY = window.scrollY;
      const scrollDirection = currentScrollY > this._lastScrollY ? "down" : "up";
      this.settoTop(scrollDirection);
    }
  }

  _setupHeaderObserver() {
    if (!this._headerContainer) return;

    if (this._headerResizeObserver) {
      this._headerResizeObserver.disconnect();
    }

    this._headerResizeObserver = new ResizeObserver(() => {
      this._setHeaderHeight(this._headerContainer.getBoundingClientRect().height);
      this._syncSectionTop();
    });

    this._headerResizeObserver.observe(this._headerContainer);
  }

  _setupVisibilityListener() {
    this._visibilityHandler = () => {
      if (document.visibilityState !== "visible") return;
      // 切回标签时按当前一级标题实际可见高度重新同步二级 nav 的 top，避免出现顶部空隙
      window.requestAnimationFrame(() => {
        this._syncSectionTop();
        window.setTimeout(() => this._syncSectionTop(), 100);
      });
    };
    document.addEventListener("visibilitychange", this._visibilityHandler);
  }

  _setupEventListeners() {
    this.menuItems.forEach((item, index) => {
      const onNavClick = (e) => this._handleMenuItemClick(e, index);
      item.element.addEventListener("click", onNavClick);
      if (item.drawerElement) {
        item.drawerElement.addEventListener("click", onNavClick);
      }
    });

    if (this._hamburgerBtn) {
      this._hamburgerBtn.addEventListener("click", () => this._toggleMenu());
    }

    this._setupNavFocusScroll();
  }

  _handleMenuItemClick(e, index) {
    const item = this.menuItems[index];
    if (!item) {
      return;
    }

    const drawerOpen = this._menuDrawer && this._menuDrawer.getAttribute("aria-hidden") === "false";

    this.currentIndex = index;
    this._updateActiveItem(index);

    if (!item.targetElement) {
      if (drawerOpen) {
        this._toggleMenu();
      }
      return;
    }

    e.preventDefault();
    this._scrollToTarget(index);

    if (drawerOpen) {
      this._toggleMenu();
    }
  }

  _scrollToSecondScreenForNavFocus() {
    const targetTop = this._getSecondScreenScrollTop();
    this._isProgrammaticScrolling = true;
    window.scrollTo({ top: targetTop, behavior: "smooth" });

    const finish = () => {
      this._lastScrollY = window.scrollY;
      this._hideHeader();
      this._checkFirstScreenVisibility();
      this._scheduleProgrammaticScrollUnlock();
    };

    if ("onscrollend" in window) {
      window.addEventListener("scrollend", finish, { once: true });
    } else {
      window.setTimeout(finish, 450);
    }
  }

  /** 键盘 Tab 进入二级导航：首项且仍在首屏时滚到第二屏；否则对齐导航条顶（顶栏 80px） */
  _setupNavFocusScroll() {
    const NAV_FOCUS_SCROLL_OFFSET = 80;

    this.querySelectorAll(".page-footer-button-list, .page-footer-menu-drawer-list").forEach((nav) => {
      const handler = (event) => {
        const related = event.relatedTarget;
        if (related instanceof Node && nav.contains(related)) return;

        const focusEl = event.target;
        const firstLink = nav.querySelector(".page-footer-button-item, .page-footer-menu-drawer-item");
        const isFirstNavLink =
          firstLink instanceof HTMLElement &&
          focusEl instanceof Node &&
          (focusEl === firstLink || firstLink.contains(focusEl));

        if (isFirstNavLink && !this._isPastFirstScreenCenter()) {
          this._scrollToSecondScreenForNavFocus();
          return;
        }

        const scrollRoot = this._sectionWrapper || this;
        const rect = scrollRoot.getBoundingClientRect();

        window.scrollTo({
          top: Math.max(0, rect.top + window.pageYOffset - NAV_FOCUS_SCROLL_OFFSET),
          behavior: "smooth",
        });
      };

      nav.addEventListener("focusin", handler);
      this._navFocusScrollCleanups.push({ nav, handler });
    });
  }

  _toggleMenu() {
    if (!this._menuDrawer || !this._hamburgerBtn) return;

    const isExpanded = this._hamburgerBtn.getAttribute("aria-expanded") === "true";
    this._hamburgerBtn.setAttribute("aria-expanded", String(!isExpanded));
    this._menuDrawer.setAttribute("aria-hidden", String(isExpanded));

    const icons = this._hamburgerBtn.querySelectorAll("svg");
    if (icons.length === 2) {
      icons[0].classList.toggle("hidden", !isExpanded);
      icons[1].classList.toggle("hidden", isExpanded);
    }
  }

  _handleHeaderVisibility() {
    const currentScrollY = window.scrollY;
    const scrollDirection = currentScrollY > this._lastScrollY ? "down" : "up";
    const hideHeaderOnScrollUp = this.dataset.hideHeaderOnScrollUp === "true";

    // Check if the first screen has passed the center line
    const isPastCenter = this._checkFirstScreenVisibility();

    if (isPastCenter) {
      if (scrollDirection === "down") {
        this._hideHeader();
      } else if (scrollDirection === "up") {
        if (hideHeaderOnScrollUp) {
          this._hideHeader();
        } else {
          this._showHeader();
        }
      }
    } else {
      this._showHeader();
    }

    this.settoTop(scrollDirection);

    if (!this._isProgrammaticScrolling) {
      this._updateActiveByScrollPosition();
    } else {
      this._scheduleProgrammaticScrollUnlock();
    }

    this._lastScrollY = currentScrollY;
  }

  settoTop(scrollDirection) {
    void scrollDirection;
    this._syncSectionTop();
  }

  _getVisibleHeaderHeight() {
    if (!this._headerContainer) return 0;

    const rect = this._headerContainer.getBoundingClientRect();
    return Math.max(0, Math.min(rect.bottom, rect.height));
  }

  _getHeaderHeight() {
    if (!this._headerContainer) return 0;

    return this._headerContainer.getBoundingClientRect().height;
  }

  _syncSectionTop() {
    if (!this._sectionWrapper) return;

    // Always position at the very top
    const nextTop = `0px`;
    if (nextTop === this._lastSectionTop) return;

    this._sectionWrapper.style.top = nextTop;
    this._lastSectionTop = nextTop;
  }

  _setHeaderHeight(height) {
    const nextHeight = String(height);
    if (nextHeight === this._lastHeaderHeight) return;

    this.dataset.headerHeight = nextHeight;
    this._lastHeaderHeight = nextHeight;
  }

  _hideHeader() {
    if (!this._headerContainer || !this._isHeaderVisible) return;

    this._headerContainer.style.transform = "translateY(-100%)";
    this._headerContainer.style.transition = "transform 0.18s ease";
    this._isHeaderVisible = false;
    this._checkFirstScreenVisibility();
    this._syncSectionTopAfterTransition();
  }

  _showHeader() {
    if (!this._headerContainer || this._isHeaderVisible) return;

    this._headerContainer.style.transform = "translateY(0)";
    this._headerContainer.style.transition = "transform 0.18s ease";
    this._isHeaderVisible = true;
    this._checkFirstScreenVisibility();
    this._syncSectionTopAfterTransition();
  }

  _syncSectionTopAfterTransition() {
    window.requestAnimationFrame(() => this._syncSectionTop());
    window.setTimeout(() => this._syncSectionTop(), 220);
  }

  _scrollToTarget(index) {
    const item = this.menuItems[index];
    if (!item || !item.targetElement) return;

    const elementRect = item.targetElement.getBoundingClientRect();
    const targetTop = elementRect.top + window.pageYOffset - window.innerHeight / 2 + elementRect.height / 2;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: "smooth",
    });

    this._isProgrammaticScrolling = true;
    this.currentIndex = index;
    this._updateActiveItem(this.currentIndex);
    this._scheduleProgrammaticScrollUnlock();
  }

  _scheduleProgrammaticScrollUnlock() {
    if (this._scrollEndTimer) {
      window.clearTimeout(this._scrollEndTimer);
    }

    // 以“滚动停止”作为解锁条件，避免固定时长导致点击后过程中的 tab 跳动
    this._scrollEndTimer = window.setTimeout(() => {
      this._isProgrammaticScrolling = false;
      this._scrollEndTimer = null;
      this._updateActiveByScrollPosition();
    }, 160);
  }

  _updateActiveByScrollPosition() {
    // 触发线位置：视口垂直中线
    const triggerLine = window.innerHeight / 2;

    let activeIndex = 0;
    let bestTop = -Infinity;
    let found = false;

    // 规则：只要中心线落在某个 section 的范围内（即该 section 的顶部已经向上越过中心线），
    // 就激活它。当多个 section 的顶部都已越过中心线时，取“最近一次越过”的那一个
    // （也就是顶部最靠近中心线的，等同于中心线现在所在的那个 section）。
    this.menuItems.forEach((item, index) => {
      if (!item.targetElement) return;

      const rect = item.targetElement.getBoundingClientRect();

      if (rect.top <= triggerLine && rect.top > bestTop) {
        bestTop = rect.top;
        activeIndex = index;
        found = true;
      }
    });

    // 没有任何 section 的顶部越过中心线（页面最顶部）→ 默认第一个
    if (!found) {
      activeIndex = 0;
    }

    if (activeIndex !== this.currentIndex) {
      this.currentIndex = activeIndex;
      this._updateActiveItem(activeIndex);
    }
  }

  _updateActiveItem(index) {
    const activeItem = this.menuItems[index];
    if (!activeItem) return;

    const setNavLinkActive = (el, isActive) => {
      if (!el) {
        return;
      }
      el.classList.toggle("active", isActive);
      if (isActive) {
        el.setAttribute("aria-current", "true");
      } else {
        el.removeAttribute("aria-current");
      }
    };

    this.menuItems.forEach((item) => {
      const isActive = item.targetId === activeItem.targetId;
      setNavLinkActive(item.element, isActive);
      setNavLinkActive(item.drawerElement, isActive);
    });
    this._scrollActiveItemIntoView();
  }

  /** 将当前激活的二级标签横向滚动到可见（居中），与页面滚动到的 target 对应 */
  _scrollActiveItemIntoView() {
    const item = this.menuItems[this.currentIndex];
    if (!item || !item.element) return;

    const el = item.element;
    const scrollHost = this; // .page-footer-button 为 overflow-x: auto 的滚动容器

    if (scrollHost.scrollWidth <= scrollHost.clientWidth) return;

    const hostRect = scrollHost.getBoundingClientRect();
    const itemRect = el.getBoundingClientRect();
    const itemCenter = itemRect.left - hostRect.left + itemRect.width / 2;
    const hostCenter = scrollHost.clientWidth / 2;
    const targetScrollLeft = scrollHost.scrollLeft + itemCenter - hostCenter;
    const maxScroll = scrollHost.scrollWidth - scrollHost.clientWidth;

    scrollHost.scrollTo({
      left: Math.max(0, Math.min(targetScrollLeft, maxScroll)),
      behavior: "smooth",
    });
  }

  _throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
};

if (!window.customElements.get("page-footer-button")) {
  window.customElements.define("page-footer-button", StickyNav);
}
